import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { categoryAPI } from "../../services/api";
import { createTextValidation } from "../common/TextValidation";

interface CategoryFormData {
  id?: string;
  name: string;
  status: string;
  image?: File | string;
}

interface CategoryDialogProps {
  open: boolean;
  onClose: () => void;
  category?: CategoryFormData | null;
  onSave: (category: CategoryFormData) => void;
  mode: "add" | "edit" | "view";
}

const schema = yup.object().shape({
  name: createTextValidation({
    title: "Category name",
    minLength: 3,
    maxLength: 100,
  }),
  status: yup.string().required("Status is required"),
});

const CategoryDialog: React.FC<CategoryDialogProps> = ({
  open,
  onClose,
  category,
  onSave,
  mode,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [mediaChanged, setMediaChanged] = useState(false);
  const [deletedCategoryMedia, setDeletedCategoryMedia] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      status: "1",
    },
  });

  useEffect(() => {
    if (category) {
      reset({
        name: category.name,
        status: category.status || "1",
      });
      if (category.image && typeof category.image === "string") {
        // Construct the full image URL using the media download API
        const imageUrl = categoryAPI.getMediaDownloadUrl(category.image);
        setPreviewUrl(imageUrl);
        setMediaChanged(false);
      }
    } else {
      reset({
        name: "",
        status: "1",
      });
      setSelectedFile(null);
      setPreviewUrl("");
      setMediaChanged(false);
    }
  }, [category, reset]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const supportedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!supportedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF)");
        event.target.value = "";
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("File size must be less than 2MB");
        event.target.value = "";
        return;
      }

      setSelectedFile(file);
      setMediaChanged(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    // Always reset input so same file can be selected again
    event.target.value = "";
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setMediaChanged(true);
    // Track deleted image name in edit mode
    if (mode === "edit" && category?.image && typeof category.image === "string") {
      setDeletedCategoryMedia(category.image);
    }
  };

  const onSubmit: SubmitHandler<CategoryFormData> = async (data) => {
    if (loading) return; // Prevent multiple submits
    setLoading(true);
    try {
      let uploadedImageName = "";

      // Step 1: Upload image if selected and media has changed
      if (selectedFile && mediaChanged) {
        const formData = new FormData();
        formData.append("fileKey", selectedFile, selectedFile.name);

        const uploadResponse = await categoryAPI.uploadCategoryMedia(formData);
        uploadedImageName = uploadResponse.data.result.files.fileKey[0].name;
      }

      // Step 2: Prepare category data
      let categoryData: any = {
        categorystatus: data.status,
        name: data.name.trim(),
      };
      if (mode === "add") {
        if (uploadedImageName) {
          categoryData.categorymedia = { name: uploadedImageName };
        }
      } else if (mode === "edit" && category?.id) {
        if (mediaChanged) {
          if (selectedFile && uploadedImageName) {
            categoryData.categorymedia = { name: uploadedImageName };
          } else if (!selectedFile) {
            // Image removed
            categoryData.categorymedia = {};
            if (deletedCategoryMedia) {
              categoryData.deletedcategorymedia = deletedCategoryMedia;
            }
          }
        }
      }

      // Step 3: Call add/update category API
      if (mode === "add") {
        await categoryAPI.addCategory(categoryData);
        toast.success("Category added successfully!");
      } else if (mode === "edit" && category?.id) {
        await categoryAPI.updateCategory(category.id, categoryData);
        toast.success("Category updated successfully!");
      }

      // Reset form and state after successful submission
      reset({
        name: "",
        status: "1",
      });
      setSelectedFile(null);
      setPreviewUrl("");
      setMediaChanged(false);

      onSave(data);
      onClose();
    } catch (error: any) {
      // Handle specific error response structure
      let errorMessage = "Failed to save category.";

      // Handle duplicate category error
      if (
        error.response?.data?.responseMessageCode === "CATEGORY_DUPLICATE" &&
        error.response?.data?.responseData?.message
      ) {
        errorMessage = error.response.data.responseData.message;
      } else if (error.response?.data?.detail?.responseData?.message) {
        errorMessage = error.response.data.detail.responseData.message;
      } else if (error.response?.data?.detail?.responseMessage) {
        errorMessage = error.response.data.detail.responseMessage;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
  reset();
  setSelectedFile(null);
  setPreviewUrl("");
  setMediaChanged(false);
  setDeletedCategoryMedia(null);
  onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {mode === "add"
          ? "Add Category"
          : mode === "edit"
          ? "Edit Category"
          : "View Category"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ position: "relative" }}>
          {/* Loader overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(255,255,255,0.7)",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              opacity: loading ? 0.5 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {/* Category Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Category Name"
                  fullWidth
                  disabled={mode === "view"}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            {/* Status Field (only for edit mode) */}
            {mode === "edit" && (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                    {errors.status && (
                      <FormHelperText>{errors.status.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Category Image
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You can upload image files up to 2MB (JPEG, PNG, GIF).
              </Typography>

              {/* Image Preview */}
              {previewUrl && (
                <Box
                  sx={{ mb: 2, position: "relative", display: "inline-block" }}
                >
                  <Avatar
                    src={previewUrl}
                    alt="Category"
                    sx={{ width: 100, height: 100, mb: 1 }}
                  />
                  {mode !== "view" && (
                    <IconButton
                      onClick={removeImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "error.main",
                        color: "white",
                        "&:hover": { bgcolor: "error.dark" },
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* Upload Button */}
              {mode !== "view" && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 1 }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{
              color: "gray",
              border: "1px solid",
              borderColor: "primary.light",
            }}
          >
            Cancel
          </Button>
          {mode !== "view" && (
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              sx={{ bgcolor: "secondary.dark" }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CategoryDialog;
